import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, MapPin, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api';

interface Property {
  id: string;
  name: string;
  address: string;
  postcode: string;
  image: string;
  status: 'Active' | 'Managed';
  totalSpend: number;
  activeOrders: number;
  lastService: string;
  city: string;
}

const PropertiesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [activeOrdersOnly, setActiveOrdersOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outstandingInvoicesPence, setOutstandingInvoicesPence] = useState(0);
  const [documentsExpiringCount, setDocumentsExpiringCount] = useState(0);

  // Fetch properties from API
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.request('/customer/properties', 'GET');
        if (response.success && Array.isArray(response.data)) {
          setProperties(response.data);
          setOutstandingInvoicesPence(response.stats?.outstandingInvoicesPence || 0);
          setDocumentsExpiringCount(response.stats?.documentsExpiringCount || 0);
        } else {
          setProperties([]);
        }
      } catch (err) {
        console.error('Failed to fetch properties:', err);
        setError('Failed to load properties. Please try again.');
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const filteredProperties = properties.filter((prop) => {
    const matchesSearch =
      prop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prop.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = !filterCity || prop.city === filterCity;
    const matchesActiveOrders = !activeOrdersOnly || prop.activeOrders > 0;
    return matchesSearch && matchesCity && matchesActiveOrders;
  });

  const citiesSet = new Set(properties.map((p) => p.city || ''));

  // Calculate stats dynamically from properties
  const stats = [
    { 
      label: 'TOTAL PROPERTIES', 
      value: properties.length.toString(), 
      color: 'text-gray-900' 
    },
    {
      label: 'ACTIVE ORDERS',
      value: properties.reduce((sum, p) => sum + (p.activeOrders || 0), 0).toString(),
      badge: 'Priority',
      color: 'text-[#00543b]',
    },
    {
      label: 'OUTSTANDING INVOICES',
      value: `£${(outstandingInvoicesPence / 100).toFixed(2)}`,
      color: 'text-[#00543b]',
    },
    {
      label: 'DOCUMENTS EXPIRING',
      value: documentsExpiringCount.toString(),
      ...(documentsExpiringCount > 0
        ? { badge: 'Action Required', badgeColor: 'bg-red-100 text-red-700', color: 'text-red-600' }
        : { color: 'text-gray-900' }),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Properties | Virewire Property Services</title>
        <meta name="description" content="Manage your properties in your Virewire customer dashboard." />
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-[#00543b] mb-2">
                Properties Overview
              </h1>
              <p className="text-gray-600">
                {loading ? 'Loading...' : `We are actively monitoring ${properties.length} ${properties.length === 1 ? 'property' : 'properties'} across your portfolio.`}
              </p>
            </div>
            <Button
              onClick={() => navigate('/register-property')}
              className="bg-[#00543b] hover:bg-[#003d2d]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((stat, idx) => (
              <Card key={idx} className="border-gray-200">
                <CardContent className="p-6">
                  <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                    {stat.label}
                  </p>
                  <div className="flex items-baseline justify-between">
                    <h3 className={`text-2xl font-bold ${stat.color}`}>
                      {stat.value}
                    </h3>
                    {stat.badge && (
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          stat.badgeColor ||
                          'bg-green-100 text-green-700'
                        }`}
                      >
                        {stat.badge}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Search & Filter */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-300"
              />
            </div>

            <select
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Filter by city</option>
              {Array.from(citiesSet).map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Active orders only
              </label>
              <button
                onClick={() => setActiveOrdersOnly(!activeOrdersOnly)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  activeOrdersOnly
                    ? 'bg-[#00543b]'
                    : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    activeOrdersOnly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
              <option>Sort by spend</option>
              <option>Highest spend</option>
              <option>Lowest spend</option>
            </select>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Loader2 className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
                <p className="text-lg font-semibold text-gray-900">
                  Loading properties...
                </p>
              </CardContent>
            </Card>
          ) : error ? (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertCircle className="h-12 w-12 text-red-300 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  Error loading properties
                </p>
                <p className="text-gray-600 mb-6">{error}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="bg-[#00543b] hover:bg-[#003d2d]"
                >
                  Try Again
                </Button>
              </CardContent>
            </Card>
          ) : filteredProperties.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {filteredProperties.slice((currentPage - 1) * 2, currentPage * 2).map((prop) => (
                  <Card
                    key={prop.id}
                    className="overflow-hidden border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/property/${prop.id}`)}
                  >
                  <div className="relative">
                      <div className="w-full h-48 bg-gradient-to-r from-[#00543b] to-[#003d2d] flex items-center justify-center">
                        <MapPin className="h-16 w-16 text-white opacity-20" />
                      </div>
                      <span className="absolute top-3 left-3 bg-[#00543b] text-white text-xs font-bold px-3 py-1 rounded">
                        Active
                      </span>
                    </div>

                    <CardContent className="p-4">
                      <h3 className="text-lg font-bold text-[#00543b] mb-1">
                        {prop.name}
                      </h3>
                      <div className="flex items-start gap-1 mb-3 text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{prop.address}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4 py-4 border-t border-b border-gray-200">
                        <div>
                          <p className="text-xs font-bold text-gray-600 uppercase">
                            Postcode
                          </p>
                          <p className="text-lg font-bold text-gray-900">
                            {prop.postcode}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-600 uppercase">
                            Type
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            {prop.property_type_name || 'Unknown'}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 border-gray-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/property/${prop.id}`);
                          }}
                        >
                          View Property
                        </Button>
                        <Button
                          className="flex-1 bg-[#00543b] hover:bg-[#003d2d]"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/dashboard/request-service');
                          }}
                        >
                          Request Service
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-2 mb-8">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  ←
                </button>
                {Array.from({
                  length: Math.ceil(filteredProperties.length / 2),
                }).map((_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => setCurrentPage(idx + 1)}
                    className={`w-10 h-10 rounded font-semibold transition-colors ${
                      currentPage === idx + 1
                        ? 'bg-[#00543b] text-white'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage(
                      Math.min(
                        Math.ceil(filteredProperties.length / 2),
                        currentPage + 1
                      )
                    )
                  }
                  disabled={
                    currentPage === Math.ceil(filteredProperties.length / 2)
                  }
                  className="p-2 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  →
                </button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  No properties found
                </p>
                <p className="text-gray-600 mb-6">
                  Try adjusting your search filters
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default PropertiesPage;
